"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { SectionBottom } from "@assets/icons";

type Turnstile = {
    render?: (
        container: string | HTMLElement,
        options: {
            sitekey: string;
            callback?: (token: string) => void;
            "expired-callback"?: () => void;
            "error-callback"?: () => void;
        },
    ) => string;
    reset?: (widgetId?: string) => void;
    remove?: (widgetId?: string) => void;
};

const Field = ({ id, label, ...rest }: React.InputHTMLAttributes<HTMLInputElement> & { id: string; label: string }) => (
    <div className="w-full md:w-1/2 mb-4">
        <label htmlFor={id} className="font-bold text-black block mb-1 mt-2 2xl:text-left">
            {label}
        </label>
        <input id={id} {...rest} className="w-full border border-gray-400 rounded px-2 py-1" />
    </div>
);

const Select = ({
    id,
    label,
    children,
    ...rest
}: React.SelectHTMLAttributes<HTMLSelectElement> & { id: string; label: string }) => (
    <div className="w-full md:w-1/2 mb-4">
        <label htmlFor={id} className="font-bold text-black block mb-1 mt-2 2xl:text-left">
            {label}
        </label>
        <select id={id} {...rest} className="w-full border border-gray-400 rounded px-2 py-1">
            {children}
        </select>
    </div>
);

function StartCareers() {
    const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!;

    const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
    const turnstileWidgetIdRef = useRef<string | null>(null);

    const [showForm, setShowForm] = useState(false);
    const [mounted, setMounted] = useState(false);

    const [turnstileToken, setTurnstileToken] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    const checkFile = (file?: File) =>
        !!file &&
        ["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(
            file.type,
        );

    const resetTurnstile = () => {
        if (typeof window === "undefined") return;

        const t = (window as Window & { turnstile?: Turnstile }).turnstile;
        const widgetId = turnstileWidgetIdRef.current;

        if (t?.reset && widgetId) t.reset(widgetId);

        // Token is no longer valid after reset/expiry.
        setTurnstileToken("");
    };

    useEffect(() => {
        if (!showForm) return;
        if (typeof window === "undefined") return;

        let cancelled = false;

        const tryRenderTurnstile = () => {
            const t = (window as Window & { turnstile?: Turnstile }).turnstile;
            const el = turnstileContainerRef.current;

            if (!t?.render || !el) return;

            // Render only once per "showForm" lifecycle
            if (!turnstileWidgetIdRef.current) {
                const widgetId = t.render(el, {
                    sitekey: TURNSTILE_SITE_KEY,
                    callback: (token) => setTurnstileToken(token),
                    "expired-callback": () => setTurnstileToken(""),
                    "error-callback": () => setTurnstileToken(""),
                });

                turnstileWidgetIdRef.current = widgetId || null;
            } else {
                // If it already exists, just reset it for a fresh token
                t.reset?.(turnstileWidgetIdRef.current);
                setTurnstileToken("");
            }
        };

        tryRenderTurnstile();

        const interval = setInterval(() => {
            if (cancelled) return;
            const t = (window as Window & { turnstile?: Turnstile }).turnstile;
            if (t?.render) {
                tryRenderTurnstile();
                clearInterval(interval);
            }
        }, 100);

        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [showForm, TURNSTILE_SITE_KEY]);

    // Optional cleanup when closing form (prevents stale widget ids / tokens)
    useEffect(() => {
        if (showForm) return;

        // When the form closes, clear token.
        setTurnstileToken("");

        // You can also remove the widget if you want a clean re-render next time.
        // (Not required, but nice.)
        if (typeof window === "undefined") return;
        const t = (window as Window & { turnstile?: Turnstile }).turnstile;
        const widgetId = turnstileWidgetIdRef.current;
        if (t?.remove && widgetId) {
            t.remove(widgetId);
            turnstileWidgetIdRef.current = null;
        }
    }, [showForm]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (isSubmitting) return;

        // Prefer callback token (most reliable).
        const token = turnstileToken;

        if (!token) {
            resetTurnstile();
            alert("Please complete the CAPTCHA.");
            return;
        }

        const f = e.currentTarget;
        const fdFromDom = new FormData(f);

        const data = new FormData();

        data.append("firstName", String(fdFromDom.get("firstName") ?? "").trim());
        data.append("lastName", String(fdFromDom.get("lastName") ?? "").trim());
        data.append("email", String(fdFromDom.get("email") ?? "").trim());
        data.append("phoneNumber", String(fdFromDom.get("phoneNumber") ?? "").trim());
        data.append("position", String(fdFromDom.get("position") ?? ""));
        data.append("referredBy", String(fdFromDom.get("referredBy") ?? ""));

        const file = fdFromDom.get("resume");
        if (!(file instanceof File) || file.size === 0 || !checkFile(file)) {
            resetTurnstile();
            alert("Only PDF or DOCX files are allowed!");
            return;
        }

        data.append("Resume", file);
        data.append("captchaToken", token);

        try {
            setIsSubmitting(true);

            const res = await fetch("https://cpl-api.blinklos.com/website/careers", {
                method: "POST",
                body: data,
            });

            if (res.ok) {
                alert("Application submitted successfully!");
                resetTurnstile();
                f.reset();
                setShowForm(false);
            } else {
                resetTurnstile();
                alert("The server rejected the application – please review your input.");
            }
        } catch {
            resetTurnstile();
            alert("Network error – please try again later.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="w-full pb-8 text-left md:text-center 2xl:scroll-pb-16">
            <div className="flex justify-center">
                <Image
                    src={SectionBottom}
                    alt="Home Two Home"
                    className="max-w-full h-auto lg:pl-[189px] w-full 2xl:h-[140px]"
                />
            </div>

            <h3 className="w-[300px] sm:w-full mx-auto text-center text-[#212459] text-[30px] font-bold leading-[40px] uppercase mb-4 md:text-xl 2xl:w-[1200px] 2xl:text-[30px] 2xl:pl-[200px]">
                COME ON BOARD TO CLEARPATH LENDING
            </h3>

            {!showForm && (
                <div className="flex justify-center mt-6 2xl:pl-[200px]">
                    <button
                        onClick={() => setShowForm(true)}
                        className="bg-[#1275ba] text-white border-2 border-[#1275ba] hover:bg-white hover:text-[#1275ba] font-semibold px-6 py-2 rounded-full text-sm uppercase"
                    >
                        Start Your Career Today
                    </button>
                </div>
            )}

            {showForm && (
                <div className="card mt-5 mb-10 mx-auto w-full max-w-[300px] md:max-w-[600px] shadow-md rounded-sm bg-[#FAFAFA] px-4 py-6 2xl:mr-[545px]">
                    <form onSubmit={handleSubmit}>
                        <div className="flex flex-col md:flex-row md:gap-4">
                            <Field
                                id="firstName"
                                name="firstName"
                                label="First Name *"
                                placeholder="First Name"
                                required
                                minLength={2}
                                maxLength={100}
                            />
                            <Field
                                id="lastName"
                                name="lastName"
                                label="Last Name *"
                                placeholder="Last Name"
                                required
                                minLength={2}
                                maxLength={100}
                            />
                        </div>

                        <div className="flex flex-col md:flex-row md:gap-4">
                            <Field
                                id="phoneNumber"
                                name="phoneNumber"
                                label="Phone *"
                                placeholder="Phone"
                                required
                                inputMode="numeric"
                                maxLength={10}
                                pattern="\d{10}"
                                onInput={(e) => {
                                    const input = e.currentTarget;
                                    input.value = input.value.replace(/\D/g, "").slice(0, 10);
                                }}
                            />

                            <Select id="position" name="position" label="Position applying for?">
                                {[
                                    "Jr. Loan Processor",
                                    "Sr. Loan Processor",
                                    "Sr. Loan Officer",
                                    "Doc Drawer",
                                    "Funder",
                                    "Post-Closing",
                                    "Administrative Assistant",
                                    "Pricing/Lock Desk",
                                    "Loan Officer Assistant",
                                ].map((p) => (
                                    <option key={p}>{p}</option>
                                ))}
                            </Select>
                        </div>

                        <div className="flex flex-col md:flex-row md:gap-4">
                            <Field
                                id="email"
                                name="email"
                                type="email"
                                label="Email *"
                                placeholder="Email"
                                required
                                maxLength={100}
                                pattern="[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$"
                                title="Please enter a valid e-mail (e.g. john.doe@example.com)"
                                autoComplete="email"
                            />
                            <Select id="referredBy" name="referredBy" label="How did you hear about us?">
                                {[
                                    "LinkedIn",
                                    "Glassdoor",
                                    "Indeed",
                                    "Zip Recruiter",
                                    "Facebook",
                                    "Twitter",
                                    "Instagram",
                                    "Referral",
                                    "Other",
                                ].map((o) => (
                                    <option key={o}>{o}</option>
                                ))}
                            </Select>
                        </div>

                        <div className="flex flex-col gap-4 w-5/6 mx-auto">
                            <div className="w-full">
                                <label htmlFor="resume" className="font-bold text-black block mb-1 mt-2 2xl:text-left">
                                    Resume *
                                </label>
                                <input
                                    id="resume"
                                    name="resume"
                                    type="file"
                                    required
                                    accept=".pdf,.docx"
                                    className="w-full border border-gray-400 rounded px-2 py-1"
                                    onChange={(e) => checkFile(e.target.files?.[0]) || (e.target.value = "")}
                                />
                            </div>

                            {mounted ? (
                                <div className="w-full max-w-[316px] my-4">
                                    <div
                                        id="careers-turnstile"
                                        ref={turnstileContainerRef}
                                        className="cf-turnstile"
                                        data-sitekey={TURNSTILE_SITE_KEY}
                                        data-action="careers"
                                    />
                                </div>
                            ) : (
                                <div className="w-full max-w-[316px] my-4" />
                            )}

                            <div className="w-full">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    aria-busy={isSubmitting}
                                    className="w-full text-white rounded-3xl font-bold py-2 px-4 bg-[#1275ba] border border-[#1275ba] hover:bg-white hover:text-black"
                                >
                                    {isSubmitting ? "Submitting..." : "Submit"}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            )}
        </section>
    );
}

export default StartCareers;
